import { ComponentProps } from 'react'
import { cn } from '~utils/cn'

export type RadioProps = Readonly<ComponentProps<'input'>>
export type RadioContainerProps = Readonly<ComponentProps<'label'>>

export const RadioContainer = ({ htmlFor, children, ...props }: RadioContainerProps) => {
  const { className } = props

  return (
    <label
      {...props}
      htmlFor={htmlFor}
      className={cn('flex w-full cursor-pointer items-center text-gray-900 text-body-1', className)}
    >
      {children}
    </label>
  )
}

/**
 * @version v1.0.0
 * @author Danny
 */
export const Radio = ({ id, disabled, checked, ...props }: RadioProps) => {
  const { className } = props

  return (
    <input
      {...props}
      id={id}
      type="radio"
      checked={checked}
      disabled={disabled}
      className={cn(
        'h-5 w-5 flex-shrink-0 cursor-pointer appearance-none rounded-full border after:content-none',
        [
          'border-gray-400',
          'checked:border-[4px] checked:border-primary-500 checked:hover:border-primary-400',
          'disabled:cursor-default disabled:border-gray-100 disabled:bg-gray-100',
          'checked:disabled:border-primary-100',
          'active:checked:border-primary-600',

          'dark:checked:bg-gray-800 dark:checked:border-secondary-800 dark:border-gray-400',
        ],
        className
      )}
    />
  )
}

/**
 * @usage
 * 아래와 같이 컴포넌트들을 조합하여 디자인에 맞게 커스텀하여 사용할 수 있습니다.
 
  type RadioItemProps = Readonly<
    Pick<RadioProps, 'value' | 'checked' | 'onChange'> &
      Pick<RadioContainerProps, 'children'>
  >;

  const RadioItem = ({
    value,
    checked,
    onChange,
    children,
  }: RadioItemProps) => {
    const id = uid(10);

    return (
      <RadioContainer
        htmlFor={id}
        className={cn(
          'rounded-lg border border-solid border-transparent px-4 py-3',
          checked && 'border-primary-500',
        )}
      >
        <Radio id={id} value={value} checked={checked} onChange={onChange} />
        <span className="ml-4">{children}</span>
      </RadioContainer>
    );
  };
 */
